import { css, cx } from "@linaria/core";
import type { ReactNode } from "react";

import {
  accordionComponentJsxPrinters,
  accordionComponentPaperPrinters,
} from "@pm2/accordion-component";
import {
  cardsComponentJsxPrinters,
  cardsComponentPaperPrinters,
} from "@pm2/cards-component";
import type {
  AccordionComponentNode,
  AudioComponentNode,
  CardsComponentNode,
  CtaComponentNode,
  ComponentFrameworkNodeTypes,
  EmptyImageComponentNode,
  GridComponentNode,
  IframeComponentFrameworkNode,
  IframeComponentNode,
  ImageComponentNode,
  PageAST,
  SliderComponentNode,
  TextComponentNode,
  Video23ComponentNode,
  SplitComponentNode,
} from "@pm2/content-engine";
import {
  ContentEnginePrinterProvider as RawContentEnginePrinterProvider,
  linkNodeToJsxPrinter,
  linkNodeToPaperPrinter,
} from "@pm2/content-engine";
import { BlockElementTypes, BlockTypes } from "@pm2/course-builder";
import {
  ctaComponentJsxPrinters,
  ctaComponentPaperPrinters,
} from "@pm2/cta-component";
import { pm2Boundaries, pm2Transitions } from "@pm2/design-tokens";
import { ElementToolbar } from "@pm2/element-toolbar";
import {
  flashcardComponentJsxPrinters,
  flashcardComponentPaperPrinters,
} from "@pm2/flashcard-component";
import {
  geogebraComponentJsxPrinters,
  geogebraComponentPaperPrinters,
} from "@pm2/geogebra-component";
import {
  gridComponentJsxPrinters,
  gridComponentPaperPrinters,
} from "@pm2/grid-component";
import {
  iArkComponentJsxPrinters,
  iArkComponentPaperPrinters,
} from "@pm2/i-ark-component";
import {
  iGuideComponentJsxPrinters,
  iGuideComponentPaperPrinters,
} from "@pm2/i-guide-component";
import {
  iframeComponentJsxPrinters,
  iframeComponentPaperPrinters,
} from "@pm2/iframe-component";
import { harmonicaIframeHeaderPaddingProp } from "@pm2/iframe-component/selectors";
import {
  imageComponentJsxPrinters,
  imageComponentPaperPrinters,
} from "@pm2/image-component";
import {
  imageHotspotsComponentJsxPrinters,
  imageHotspotsComponentPaperPrinters,
} from "@pm2/image-hotspots-component";
import {
  mediaComponentJsxPrinters,
  mediaComponentPaperPrinters,
} from "@pm2/media-component";
import {
  opgavekompleksetComponentJsxPrinters,
  opgavekompleksetPaperPrinters,
} from "@pm2/opgavekomplekset-component";
import {
  sliderComponentJsxPrinters,
  sliderComponentPaperPrinters,
} from "@pm2/slider-component";
import {
  splitComponentJsxPrinters,
  splitComponentPaperPrinters,
} from "@pm2/split-component";
import {
  textComponentJsxPrinters,
  textComponentPaperPrinters,
} from "@pm2/text-component";
import {
  AddToCourseBuilder,
  Ribbon,
  componentFrameworkErrorJsxPrinters,
  componentFrameworkErrorPaperPrinters,
} from "@pm2/ui-components";
import { ribbonTokens } from "@pm2/ui-components/selectors";
import { asideTokens } from "@pm2/ui-layout/selectors";
import type {
  OffsetPrintChunk,
  PrintPage,
  PrintSection,
} from "@provider/print";
import { units } from "@ui/css";
import type {
  ASTPrinterMap,
  ASTPrinterInterceptor,
  ExtractNodesFromAST,
  ASTPrintContext,
} from "@utils/ast";
import { generateJsxKey } from "@utils/ast";

import { pageTokens } from "../pages";

/**
 * Handles the creation of the course builder store as soon as the currently
 * active user becomes available.
 */
export function ContentEnginePrinterProvider(props: {
  children: ReactNode;
}): React.JSX.Element {
  return (
    <RawContentEnginePrinterProvider
      printers={printers}
      paperPrinters={paperPrinters}
      interceptor={interceptor}
    >
      {props.children}
    </RawContentEnginePrinterProvider>
  );
}

const printers: ASTPrinterMap<PageAST[number], React.JSX.Element> = {
  ...accordionComponentJsxPrinters,
  ...cardsComponentJsxPrinters,
  ...componentFrameworkErrorJsxPrinters,
  ...ctaComponentJsxPrinters,
  ...flashcardComponentJsxPrinters,
  ...geogebraComponentJsxPrinters,
  ...gridComponentJsxPrinters,
  ...iArkComponentJsxPrinters,
  ...iframeComponentJsxPrinters,
  ...iGuideComponentJsxPrinters,
  ...imageComponentJsxPrinters,
  ...imageHotspotsComponentJsxPrinters,
  ...mediaComponentJsxPrinters,
  ...opgavekompleksetComponentJsxPrinters,
  ...sliderComponentJsxPrinters,
  ...splitComponentJsxPrinters,
  ...textComponentJsxPrinters,
  link: linkNodeToJsxPrinter,
};

const paperPrinters: ASTPrinterMap<
  PageAST[number],
  PrintSection | PrintPage[] | OffsetPrintChunk[]
> = {
  ...accordionComponentPaperPrinters,
  ...cardsComponentPaperPrinters,
  ...componentFrameworkErrorPaperPrinters,
  ...ctaComponentPaperPrinters,
  ...flashcardComponentPaperPrinters,
  ...geogebraComponentPaperPrinters,
  ...gridComponentPaperPrinters,
  ...iArkComponentPaperPrinters,
  ...iframeComponentPaperPrinters,
  ...iGuideComponentPaperPrinters,
  ...imageComponentPaperPrinters,
  ...imageHotspotsComponentPaperPrinters,
  ...mediaComponentPaperPrinters,
  ...opgavekompleksetPaperPrinters,
  ...sliderComponentPaperPrinters,
  ...splitComponentPaperPrinters,
  ...textComponentPaperPrinters,
  link: linkNodeToPaperPrinter,
};

/**
 * interceptor that wraps content elements, adding toolbar, ribbon and other
 * surrounding UIs and features.
 */
const interceptor: ASTPrinterInterceptor<
  PageAST[number],
  React.JSX.Element,
  {
    disableCourseBuilderAddButton?: boolean;
    disableElementToolbar?: boolean;
  }
> = (input, output, context) => {
  const WrapperComponent = determineWrapperComponent(input, context);

  if (!WrapperComponent) {
    return output;
  }

  const ribbonIconId = "pm2" in input ? input.pm2.ribbonIconId : undefined;
  const showRibbon = (() => {
    switch (input.nodeType) {
      case "iframe-component":
      case "iframe-component.component-framework":
        return (
          !input.options.autoActivate &&
          input.options.desiredDisplayMode !== "harmonica"
        );

      case "component-framework.error":
      case "component-framework.flashcard":
      case "component-framework.geogebra":
      case "component-framework.i-ark":
      case "component-framework.i-guide":
        return input.options.displayMode === "lightbox";

      default:
        return true;
    }
  })();

  const isScrambled = "pm2" in input ? input.pm2.isScrambled : false;

  return (
    <WrapperComponent
      key={generateJsxKey(context)}
      data-component-wrapper
      data-component={isComponent(input)}
      data-content-component={isContentComponent(input)}
      data-component-framework-component={isComponentFrameworkNode(input)}
      data-node-id={"id" in input ? input.id : undefined}
      data-type={input.nodeType}
      className={cx(
        componentWrapperContentCls,
        "pm2" in input && input.pm2.ribbonIconId !== undefined && "withRibbon",
      )}
    >
      {ribbonIconId !== undefined && showRibbon && (
        <Ribbon ident={ribbonIconId} />
      )}

      {!context.disableCourseBuilderAddButton &&
        renderCourseBuilderAddButton(input)}
      {!context.disableElementToolbar &&
        isComponent(input) &&
        !handlesElementToolbarInternally(input) &&
        !isScrambled && <ElementToolbar node={input} />}

      {output}
    </WrapperComponent>
  );
};

/**
 * render the add to course builder basket utility if it's enabled on the
 * portal and the user is currently signed in as a super user.
 */
function renderCourseBuilderAddButton(
  input: ExtractNodesFromAST<PageAST[number]>,
): React.JSX.Element | null {
  if (
    !("pm2" in input) ||
    !input.pm2.learningObjectId ||
    !input.pm2.learningObjectTitle
  ) {
    return null;
  }

  // translate nodeType into the expected BlockElementTypes enum for the
  // course builder to be fully compatible with the content-engine AST
  const elementType = (() => {
    switch (input.nodeType) {
      case "cta-component":
        return BlockElementTypes.COMPLEXT_CONTENT;

      case "audio-component":
        return BlockElementTypes.AUDIO;

      case "iframe-component":
      case "iframe-component.component-framework":
      case "component-framework.geogebra":
      case "component-framework.flashcard":
      case "component-framework.i-guide":
      case "component-framework.i-ark":
      case "component-framework.opgavekomplekset":
      case "component-framework.image-hotspots":
        return BlockElementTypes.IFRAME;

      case "image-component":
        return BlockElementTypes.IMAGE;

      case "slider-component":
        return BlockElementTypes.SLIDER;

      case "text-component":
        return BlockElementTypes.RICHTEXT;

      case "23-video-component":
        return BlockElementTypes.VIDEO;
    }
  })();

  if (!elementType) {
    return null;
  }

  return (
    <AddToCourseBuilder
      block={{
        type: BlockTypes.REFERENCE,
        element: {
          id: input.pm2.learningObjectId,
          title: input.pm2.learningObjectTitle,
          type: elementType,
        },
      }}
    />
  );
}

/**
 * determine which component type to use when wrapping output (either a plain
 * div, or a centering component depending on whether we're in the root of the
 * render tree).
 */
function determineWrapperComponent(
  input: ExtractNodesFromAST<PageAST[number]>,
  context: ASTPrintContext<
    ExtractNodesFromAST<PageAST[number]>,
    React.JSX.Element
  >,
): React.ComponentType<{ className?: string }> | "div" | null {
  if (isComponentFrameworkNode(input)) {
    return ComponentWrapper;
  }

  if (!isComponent(input)) {
    // for all non-native PM2 nodes, don't provide any kind of wrapping
    return null;
  }

  if (context.depth !== 0) {
    // nested elements should just be wrapped inside a plain container, as we
    // don't want ot do any other kind of special handling
    return ComponentWrapper;
  }

  if (
    (input.nodeType === "slider-component" &&
      input.options.displayType !== "default") ||
    input.nodeType === "split-component" ||
    input.nodeType === "grid-component" ||
    input.nodeType === "cards-component" ||
    input.nodeType === "cta-component"
  ) {
    return FullWidthWrapper;
  }

  if (input.nodeType === "image-component" && input.options.enableFullWidth) {
    return FullWidthWrapper;
  }

  if (
    input.nodeType === "iframe-component" ||
    input.nodeType === "iframe-component.component-framework"
  ) {
    if (input.options.desiredDisplayMode === "harmonica") {
      return HarmonicaIframeWrapper;
    } else if (input.options.autoActivate) {
      return FullWidthWrapper;
    }
  }

  return TextColumnWrapper;
}

function isComponent(
  input: ExtractNodesFromAST<PageAST[number]>,
): input is
  | AccordionComponentNode
  | AudioComponentNode
  | CardsComponentNode
  | CtaComponentNode
  | EmptyImageComponentNode
  | GridComponentNode
  | IframeComponentFrameworkNode
  | IframeComponentNode
  | ImageComponentNode
  | SliderComponentNode
  | SplitComponentNode
  | TextComponentNode
  | Video23ComponentNode
  | ComponentFrameworkNodeTypes {
  switch (input.nodeType) {
    case "accordion-component":
    case "audio-component":
    case "cards-component":
    case "cta-component":
    case "grid-component":
    case "iframe-component":
    case "iframe-component.component-framework":
    case "empty-image-component":
    case "image-component":
    case "slider-component":
    case "text-component":
    case "split-component":
    case "23-video-component":
    case "component-framework.error":
    case "component-framework.i-guide":
    case "component-framework.flashcard":
    case "component-framework.geogebra":
      return true;

    default:
      return false;
  }
}

function isContentComponent(
  input: ExtractNodesFromAST<PageAST[number]>,
): input is
  | AudioComponentNode
  | EmptyImageComponentNode
  | ComponentFrameworkNodeTypes
  | IframeComponentFrameworkNode
  | IframeComponentNode
  | ImageComponentNode
  | Video23ComponentNode
  | TextComponentNode
  | CtaComponentNode {
  switch (input.nodeType) {
    case "audio-component":
    case "iframe-component":
    case "iframe-component.component-framework":
    case "empty-image-component":
    case "image-component":
    case "text-component":
    case "23-video-component":
    case "cta-component":
    case "component-framework.error":
    case "component-framework.i-guide":
    case "component-framework.flashcard":
      return true;

    default:
      return false;
  }
}

function handlesElementToolbarInternally(
  input: ExtractNodesFromAST<PageAST[number]>,
) {
  return (
    input.nodeType === "slider-component" || isComponentFrameworkNode(input)
  );
}

function isComponentFrameworkNode(
  input: ExtractNodesFromAST<PageAST[number]>,
): input is ComponentFrameworkNodeTypes {
  return input.nodeType.startsWith("component-framework.");
}

const componentWrapperCls = css`
  /**
   * add vertical margin between nested elements inside.
   */
  position: relative;
  margin-top: ${units.px(40)};
  margin-bottom: ${units.px(30)};

  &:has([data-ibook-button="true"]) {
    margin-top: ${units.px(70)};
  }

  &:is(
    [data-type="cta-component"],
    [data-type="cards-component"],
    [data-type="split-component"]
  ) {
    margin-block: ${units.px(80)};
  }

  &:first-child {
    margin-top: 0;

    & > .gAccordion,
    & > .gAudio,
    & > .gEmptyImage,
    & > .gIframe,
    & > .gImage,
    & > .gPlaceholder,
    & > .gRichText,
    & > .gSlider,
    & > .gText,
    & > .gVideo {
      margin-top: 0;
    }
  }

  &:last-child {
    margin-bottom: 0;

    & > .gAccordion,
    & > .gAudio,
    & > .gEmptyImage,
    & > .gIframe,
    & > .gImage,
    & > .gPlaceholder,
    & > .gRichText,
    & > .gSlider,
    & > .gText,
    & > .gVideo {
      margin-bottom: 0;
    }
  }
`;

const componentWrapperContentCls = css`
  position: relative;
`;

const ComponentWrapper: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ className, children, ...props }) => {
  return (
    <div className={cx(className, componentWrapperCls)} {...props}>
      {children}
    </div>
  );
};

const harmonicaIframeWrapperCls = css`
  /** force ribbon to remain static when expanding the iframe */
  ${ribbonTokens.__override({ top: units.px(12) }).toString()}

  /** push description when a ribbon is rendeded */
  &.withRibbon {
    ${harmonicaIframeHeaderPaddingProp}: 23px;
  }
`;

const HarmonicaIframeWrapper: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ className, children, ...props }) => {
  return (
    <div
      className={cx(
        className,
        componentWrapperCls,
        harmonicaIframeWrapperCls,
        textColumnWrapperCls,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const textColumnWrapperCls = css`
  ${pm2Boundaries.element
    .__override({
      offsetLeft: `max(0px, calc((${pm2Boundaries.container.width()} - ${pm2Boundaries.textColumn.width()}) / 2))`,
      offsetRight: `max(0px, calc((${pm2Boundaries.container.width()} - ${pm2Boundaries.textColumn.width()}) / 2))`,
    })
    .toString()};

  margin-right: auto;
  margin-left: auto;

  max-width: ${pm2Boundaries.textColumn.width()};

  transition: ${pm2Transitions.asideCollapse()};

  /**
   * in case the navigation aside is open, then do a best effort to not overlap
   * content while still not 1) shrinking cotent and 2) respecting global page
   * paddings.
   *
   * NB: The transformation below only moves textColumns (700px) within the
   * page container (1100px) and attempts to stay the page-padding away from
   * the left-aside. The transformation is only triggered in cases wherein
   * text-content will be overlapped even after the general page
   * transformation.
   */
  @media (max-width: 2200px) {
    body.aside--left:not(.aside--left--prevent-overlap) & {
      transform: translateX(
        clamp(
          0px,
          calc(
            (
                ${asideTokens.leftAsideWidth()} +
                  ${pm2Boundaries.root.paddingLeft()}
              ) -
              (
                ${pm2Boundaries.element.offsetLeft()} +
                  ${pm2Boundaries.container.offsetLeft()} +
                  ${pageTokens.asideLeftTransformation()}
              )
          ),
          ${pm2Boundaries.element.offsetRight()}
        )
      );
    }

    .${componentWrapperCls} &&& {
      transform: unset;
    }
  }
`;

const TextColumnWrapper: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ className, children, ...props }) => {
  return (
    <div
      className={cx(className, componentWrapperCls, textColumnWrapperCls)}
      {...props}
    >
      {children}
    </div>
  );
};

const FullWidthWrapper: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ className, children, ...props }) => {
  return (
    <div className={cx(className, componentWrapperCls)} {...props}>
      {children}
    </div>
  );
};
